import styled from 'styled-components';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import {
  BsRsvGallery,
  IMediaGalleryItem,
  SlideHeader,
} from '@hotelplan/core.serving.bs-rsv-gallery';
import { Link } from '@hotelplan/components.common.link';
import { Container } from 'components/domain/container';
import { cutTextByLinesCount } from '@hotelplan/components.common.mixins';

export const ProductGalleryWrapper = styled.div(({ theme: { colors } }) => ({
  background: colors.darkGrey,
  padding: '14px 0 14px 0',
}));

export const SectionMainTitle = styled.h2(
  ({ theme: { media, fontSizes, colors } }) => ({
    fontSize: '46px',
    fontWeight: '800',
    lineHeight: '60px',
    textAlign: 'center',
    maxWidth: '818px',
    margin: '0 auto',
    marginTop: '66px',
    color: colors.white,
    [media.mobile]: {
      fontSize: fontSizes[7],
      lineHeight: '42px',
      marginTop: '36px',
    },
  })
);

export const ProductGalleryContent = styled(Container)<{
  slideInfoHeight: number;
}>(({ slideInfoHeight }) =>
  sx2CssThemeFn({
    display: ['static', 'flex'],
    gap: ['initial', '20px'],
    maxWidth: '1480px',
    my: ['32px', '66px'],
    '@media screen and (max-width: 896px)': {
      mb: `${slideInfoHeight + 36}px`,
    },
    px: [3, '20px'],
  })
);

export const ProductNameTitle = styled.span({
  fontWeight: 'bold',
});

export const ProductSlideHeader = styled(SlideHeader)(
  ({ theme: { media, fontSizes, space, colors } }) => ({
    display: 'flex',
    position: 'relative',
    left: 0,
    top: '-39px',
    fontSize: fontSizes[1],
    color: 'white',
    [media.mobile]: {
      top: '-30px',
    },
    '&.overlay': {
      width: '80%',
      left: 'calc(15% / 2)',
      top: space[4],
      '~ .slide-info': {
        background: `rgba(0, 0, 0, 0.5)`,
        [media.mobile]: {
          padding: space[3],
        },
      },
      [media.mobile]: {
        top: '20px',
        left: space[3],
      },
    },
  })
);

export const ProductDescription = styled.div(
  sx2CssThemeFn({
    marginTop: '12px',
    '&.product-description': {
      '.slide-title': {
        fontWeight: '700',
        '&:after': {
          content: `" - "`,
          fontWeight: 400,
        },
      },
    },
  })
);

export const ProductLink = styled(Link)(
  sx2CssThemeFn({
    display: 'flex',
    position: 'relative',
    color: 'white',
    overflow: ['hidden', `unset`],
    textOverflow: [`ellipsis`, `unset`],
    width: ['100%', 'auto'],
    paddingBottom: '4px',
    '.icon': {
      width: '22px',
      height: '22px',
      verticalAlign: 'sub',
      marginRight: '10px',
    },
    '&::after': {
      position: 'absolute',
      bottom: '0',
      right: 0,
      display: 'block',
      height: '2px',
      content: '""',
      width: 'calc(100% - 32px)',
      backgroundColor: 'white',
      opacity: 0.2,
      transition: '0.2s ease-in',
    },
    '&::before': {
      position: 'absolute',
      bottom: '0',
      left: '32px',
      display: 'block',
      height: '2px',
      content: '""',
      width: '2px',
      backgroundColor: 'white',
    },

    '&:hover': {
      color: 'white',
      '&::after': {
        opacity: 1,
      },
      '.icon': {
        fill: 'white',
      },
    },
  })
);

export const ProductMediaGalleryContainer = styled.div(
  sx2CssThemeFn({
    position: 'relative',
    flex: 1,
    mt: ['54px', 0],
  })
);

export const ResponsiveProductMediaGallery = styled(BsRsvGallery)<{
  galleryHeight: number;
}>(({ galleryHeight }) =>
  sx2CssThemeFn({
    maxHeight: '640px',
    '.slide-info': {
      position: 'absolute',
      bg: 'rgba(0, 0, 0, 0.5)',
      width: '100%',
      bottom: 0,
      '@media screen and (max-width: 896px)': {
        bottom: 'initial',
        top: `${galleryHeight}px`,
      },
      '.product-slide-info': {
        position: 'relative',
        padding: 0,
        fontSize: 1,
        marginTop: ['6px', '12px'],
        '&:before': {
          content: 'none',
        },
      },
      '.slide-title': {
        fontWeight: '700',
      },
      '@media only screen and (max-width: 480px)': {
        '.slide-title-disruptor, .slide-description': {
          display: 'none',
        },
      },
    },
    '.open-preview': {
      mr: [3, '40px'],
      mb: 3,
      '.icon': {
        fill: 'transparent',
        width: '32px',
        height: '32px',
      },
      '&:hover, &:focus': {
        backgroundColor: 'transparent',
      },
    },
    '.bs-icon-swipe': {
      display: ['inline-block', 'none'],
    },
  })
);

export const ProductSlideFooter = styled.div(
  sx2CssThemeFn({
    maxWidth: '940px',
    fontSize: 1,
    lineHeight: '22px',
    padding: ['16px 20px', '16px 0 16px 40px'],
    '&.overlay': {
      padding: 0,
    },
  })
);

export const ProductGalleryPreviewContainer = styled.div(
  ({ theme: { colors, space } }) =>
    sx2CssThemeFn({
      position: 'relative',
      marginRight: [`-${space[3]}`, 0],
      '&:after': {
        content: '""',
        display: ['none', 'block'],
        height: '100px',
        marginRight: '10px',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(180deg, ${colors.darkGrey}00 0%, ${colors.darkGrey}FF 100%);`,
        zIndex: 5,
      },
    })
);

export const ProductGalleryPreviewItemList = styled.div<{
  galleryHeight: number;
}>(({ galleryHeight, theme: { colors } }) =>
  sx2CssThemeFn({
    width: ['initial', '356px'],
    height: ['initial', '100%'],
    maxHeight: ['initial', `${galleryHeight}px`],
    padding: ['0 0 10px 0', '0 2px 120px'],
    display: 'flex',
    flexDirection: ['row', 'column'],
    gap: '8px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },
    '&::-webkit-scrollbar-track': {
      border: `1px solid ${colors.secondary}4D`,
      borderRadius: '3px',
      backgroundColor: `${colors.secondary}4D`,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '3px',
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
    },
  })
);

export const ProductGalleryPreviewItem = styled.a(({ theme: { colors } }) =>
  sx2CssThemeFn({
    minWidth: ['234px', 'initial'],
    marginRight: '5px',
    display: 'flex',
    gap: '10px',
    backgroundColor: `${colors.secondary}4D`,
    borderRadius: '5px',
    border: '2px solid',
    borderColor: 'transparent',
    '&.active, &:hover': {
      borderColor: colors.borderColor,
    },
    color: colors.white,
  })
);

export const ProductGalleryPreviewItemImage = styled.div({
  width: '78px',
  height: '78px',
  position: 'relative',
});

export const ProductGalleryPreviewItemTextWrapper = styled.div({
  flex: 1,
  padding: '6px 10px 6px 0',
});

export const ProductGalleryPreviewItemText = styled.div({
  height: '4.125em',
  lineHeight: 1.375,
  ...cutTextByLinesCount(3),
});
